import { useMemo } from 'react';

import type { ConfigKey, PearlChainUpdateType, RouteStopType } from '@/types';

import useGetRoutesDeviationMap from '@/hooks/use-get-routes-deviation-map';
import { useMapConfig } from '@/hooks/use-map-config';
import isSameDayAsToday from '@/utils/helpers/is-same-day-as-today';

export const useProcessedStops = (
  stopsData: RouteStopType[],
  pearlChainData?: PearlChainUpdateType | null,
  journeyDate?: string,
  journeyId?: string
) => {
  const { config } = useMapConfig();
  const shouldHideDetails = config['hideDetails' as ConfigKey];
  const selectedDate = journeyDate ?? '';

  const { activeJourneyIds } = useGetRoutesDeviationMap();

  const isActive = journeyId ? activeJourneyIds?.includes(journeyId) : false;
  const isToday = isSameDayAsToday(new Date(selectedDate));

  const dataSource = useMemo(() => {
    const baseSource = isActive || isToday ? (pearlChainData?.stops ?? []) : stopsData;

    if (shouldHideDetails) {
      return baseSource.filter((stop) => stop.stoppingState !== 'never_stop');
    }

    return baseSource;
  }, [isToday, isActive, pearlChainData?.stops, stopsData, shouldHideDetails]);

  const { passedStops, remainingStops } = useMemo(() => {
    let isRemaining = false;

    return dataSource.reduce(
      (acc, stop) => {
        if (!isRemaining && (stop.state === 'Arrived' || stop.state === 'Expected')) {
          isRemaining = true;
        }

        const target = isRemaining ? acc.remainingStops : acc.passedStops;
        target.push(stop);

        return acc;
      },
      { passedStops: [] as RouteStopType[], remainingStops: [] as RouteStopType[] }
    );
  }, [dataSource]);

  return { dataSource, passedStops: passedStops, remainingStops };
};
